import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldProps } from "@insite/mobius/TextField";
import ProductPromotionTextBanner, {
    ProductPromotionTextBannerStyles,
} from "@store/Components/Store/ProductPromotionTextBanner";
import ProductAddToCartButton from "@store/Overrides/Components/ProductAddToCartButton"; // Store Custom Code
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@store/Overrides/Components/ProductQuantityBreakPricing";
import { dispatchProductClickEvent } from "@store/Services/AnalyticsService"; // Store Custom Code
import { COLORS, TYPOGRAPHY } from "@store/theme/design";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface ProductSubAssemblyStyles {
    viewProductLink?: LinkPresentationProps;
    loginLink?: LinkPresentationProps;
    productImageStyles?: ProductImageStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    productQuantityOrderedStyles?: TextFieldProps;
    rowStyles?: DataTableRowProps;
    productBrandStyles?: ProductBrandStyles;
    productPromotionTextBannerStyles?: ProductPromotionTextBannerStyles;
}

interface OwnProps {
    id: string;
    productNumber: string;
    productTitle: string;
    product: ProductModel;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, "subAssemblyItems", ownProps.id),
    signInUrl: getPageLinkByPageType(state, "SignInPage"),
    userName: state.context.session?.userName,
});

const mapDispatchToProps = {
    updateProductInfo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const productSubAssemblyStyles: ProductSubAssemblyStyles = {
    productImageStyles: {
        img: {
            css: css`
                height: 55px;
                width: auto;
                border: 1px solid ${COLORS.border.base};
            `,
        },
    },
    rowStyles: {
        css: css`
            td {
                padding-top: 1em;
                padding-bottom: 1em;
            }
        `,
    },
    viewProductLink: {
        typographyProps: {
            underline: true,
            size: "15px",
        },
        hoverStyle: {
            textDecorationLine: "none",
        },
    },
    loginLink: {
        typographyProps: {
            underline: true,
            size: "15px",
        },
        hoverStyle: {
            textDecorationLine: "none",
        },
    },
    quantityBreakPricing: {
        viewLink: {},
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
                margin-right: 1em;
                height: 40px;
                width: 50px;
            `,
            inputSelect: css`
                height: 40px;
                width: 50px;
                text-align: center;
            `,
        },
    },
    productBrandStyles: {
        nameLink: {
            typographyProps: {
                size: "15px",
                weight: 500,
            },
        },
        nameText: {
            size: "15px",
            weight: 500,
        },
    },
    productPromotionTextBannerStyles: {
        wrapper: {
            css: css`
                display: inline-block;
                position: static;
                z-index: 1;
                padding: 0.25em 0.5em;
                text-align: center;
                align-items: center;
                margin-bottom: 4px;
            `,
        },
        label: {
            size: TYPOGRAPHY.size.veryTiny,
            color: "white",
            css: css`
                display: block;
                font-weight: bold;
                text-transform: uppercase;
                margin-bottom: 0;
            `,
        },
    },
};

const styles = productSubAssemblyStyles;

const ProductSubAssemblyProduct: React.FC<Props> = ({
    productNumber,
    productTitle,
    productInfo,
    signInUrl,
    userName,
    product,
    updateProductInfo,
}) => {
    if (!product || !productInfo) {
        return null;
    }
    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: "subAssemblyItems", productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: "subAssemblyItems", productId: product.id, unitOfMeasure });
    };
    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };
    // Store Custom Code - Analytics
    const handleAnalyticsClick = () => {
        dispatchProductClickEvent(productContext);
    };
    return (
        <>
            <ProductContext.Provider value={productContext}>
                <DataTableRow {...styles.rowStyles}>
                    <DataTableCell>
                        <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                    </DataTableCell>
                    <DataTableCell>
                        <div>
                            <ProductPromotionTextBanner
                                extendedStyles={styles.productPromotionTextBannerStyles}
                                product={productContext}
                            ></ProductPromotionTextBanner>
                        </div>
                        <Link
                            {...styles.viewProductLink}
                            href={productInfo?.productDetailPath}
                            onClick={handleAnalyticsClick} // Store Custom Code
                        >
                            {productTitle}
                        </Link>
                    </DataTableCell>
                    <DataTableCell>
                        {product.brand && (
                            <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                        )}
                    </DataTableCell>
                    <DataTableCell>{productNumber}</DataTableCell>
                    {userName ? (
                        <>
                            <DataTableCell>
                                <ProductQuantityOrdered
                                    extendedStyles={styles.productQuantityOrderedStyles}
                                    labelOverride=""
                                    id="subAssemblyItems-qty"
                                />
                                <ProductQuantityBreakPricing
                                    extendedStyles={styles.quantityBreakPricing}
                                    isModal={true}
                                />
                            </DataTableCell>
                            <DataTableCell>
                                {productInfo?.pricing?.additionalResults?.priceMinusCorePrice
                                    ? productInfo?.pricing?.additionalResults?.priceMinusCorePrice
                                    : productInfo?.pricing?.unitRegularPriceDisplay}
                                {productInfo?.unitOfMeasure && <>/{productInfo?.unitOfMeasure}</>}
                            </DataTableCell>
                        </>
                    ) : (
                        <DataTableCell>
                            <Link {...styles.loginLink} href={signInUrl?.url}>
                                {translate("Please log in to get prices")}
                            </Link>
                        </DataTableCell>
                    )}
                    <DataTableCell>
                        {/* Store Custom Code */}
                        <ProductAddToCartButton
                            data-test-selector="addToCartBtn"
                            analyticsId="ProductSubAssemblyProduct"
                        />
                    </DataTableCell>
                </DataTableRow>
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductSubAssemblyProduct);
